import React, { useState, useMemo, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckIcon, FileTextIcon, SendIcon, SaveIcon, CalendarClockIcon, BookmarkPlusIcon, StarIcon, AlertTriangleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { AudienceSelector } from './notice/AudienceSelector';
import { ChannelEditor } from './notice/ChannelEditor';
import { NoticePreview, type PreviewTab } from './notice/NoticePreview';
import { PreviewMiniPanel } from './notice/PreviewMiniPanel';
import { SendScheduler } from './notice/SendScheduler';
import { VariablesBlock } from './notice/VariablesBlock';
import { AtnaBrief } from './notice/AtnaBrief';
import { AiReviewPanel } from './notice/AiReviewPanel';
import { SaveTemplateDialog } from './notice/SaveTemplateDialog';
import type { AtnaDraft } from '../utils/atna';
import { stripHtml } from './RichTextEditor';
import { auditNotice, generateForChannel, isChannelEmpty, type AiContext, type AuditFinding } from '../utils/aiAssist';
import { getIncompleteVariables } from '../utils/variables';
import { getActiveCity } from '../utils/activeCity';
import { SYSTEM_VARIABLES, type NoticeVariable } from '../types/variables';
import { CATEGORIES, sectorsFor, getCategoryLabel, getTemplateCategory, type NoticeTemplate } from '../data/communications';
import { EMPTY_AUDIENCE, EMPTY_CHANNELS, EMPTY_SCHEDULE, type AudienceState, type ChannelsState, type ScheduleState } from '../types/composer';
import { describeAudience, getAudienceTotal } from '../utils/audience';
import type { Notice } from '../types/notice';

/** Converts template plain text into the HTML the rich editors expect. */
function textToHtml(text: string, imageUrl?: string): string {
  const paragraphs = text.split('\n\n').map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`).join('');
  const image = imageUrl ? `<img src="${imageUrl}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />` : '';
  return `${image}${paragraphs}`;
}

/**
 * Custom (per-notice) variables are temporarily hidden. System variables
 * (citizen first/last name, today's date) stay insertable via { }.
 */
const SHOW_CUSTOM_VARIABLES = false;
interface NoticeComposerProps {
  legacyMode: boolean;
  onToggleLegacy: (value: boolean) => void;
  /** Language the notice content is being written in — never the interface. */
  contentLang: 'fr' | 'en';
  /** Switches which language version of the content is being edited. */
  onContentLangChange: (value: 'fr' | 'en') => void;
  /** Notice being edited, or null when composing a new one. */
  editingNotice?: Notice | null;
  /** Every template, built-in or saved by the team, ids prefixed by category. */
  templates: NoticeTemplate[];
  /** Creates a template, or replaces `replacesId` when editing one. */
  onSaveTemplate: (template: NoticeTemplate, replacesId?: string) => void;
  onSubmit: (mode: 'draft' | 'send') => void;
  /** Composes a reusable template instead of a notice — no audience, no sending. */
  templateMode?: boolean;
  /** Template being edited in template mode, or null when creating one. */
  editingTemplate?: NoticeTemplate | null;
}
export function NoticeComposer({
  legacyMode,
  onToggleLegacy,
  contentLang,
  onContentLangChange,
  editingNotice = null,
  templates,
  onSaveTemplate,
  onSubmit,
  templateMode = false,
  editingTemplate = null
}: NoticeComposerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [category, setCategory] = useState(editingNotice?.category ?? '');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [title, setTitle] = useState(editingNotice?.title ?? '');
  const [description, setDescription] = useState('');
  /**
   * The notice is multilingual: each language keeps its own content, while the
   * set of active channels stays shared between the two versions.
   */
  const [channelsByLang, setChannelsByLang] = useState<Record<'fr' | 'en', ChannelsState>>({
    fr: EMPTY_CHANNELS,
    en: EMPTY_CHANNELS
  });
  const channels = channelsByLang[contentLang];

  /** Writes to the language version currently being edited. */
  const setChannels = (updater: ChannelsState | ((prev: ChannelsState) => ChannelsState)) => setChannelsByLang(prev => ({
    ...prev,
    [contentLang]: typeof updater === 'function' ? (updater as (p: ChannelsState) => ChannelsState)(prev[contentLang]) : updater
  }));
  const [audience, setAudience] = useState<AudienceState>(EMPTY_AUDIENCE);
  const [schedule, setSchedule] = useState<ScheduleState>(EMPTY_SCHEDULE);
  /** Variables live only for this notice — never saved to the configuration. */
  const [variables, setVariables] = useState<NoticeVariable[]>(SYSTEM_VARIABLES);
  const [previewTab, setPreviewTab] = useState<PreviewTab>('push');
  /**
   * Preview panel, docked full-height on the right like a split-screen assistant:
   * "split" = writing | preview side by side, "full" = preview only, "closed" = writing only.
   */
  const [previewMode, setPreviewMode] = useState<'split' | 'full' | 'closed'>('split');
  /** Share of the content area given to the preview in split mode. */
  const [previewRatio, setPreviewRatio] = useState(0.45);
  const rootRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Track the main content area (right of the app sidebar) the preview docks into.
  useLayoutEffect(() => {
    const main = rootRef.current?.closest('main') as HTMLElement | null;
    if (!main) return;
    const measure = () => {
      const r = main.getBoundingClientRect();
      setArea({
        top: r.top,
        left: r.left,
        width: r.width
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(main);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  const docked = !!area && area.width >= 1000;
  const panelWidth = area ? Math.round(area.width * previewRatio) : 0;

  // Make room for the docked preview: the whole page (header included) shrinks to its left.
  useLayoutEffect(() => {
    const main = rootRef.current?.closest('main') as HTMLElement | null;
    if (!main) return;
    main.style.paddingRight = docked && previewMode === 'split' ? `${panelWidth}px` : '';
    return () => {
      main.style.paddingRight = '';
    };
  }, [docked, previewMode, panelWidth]);

  // Esc leaves full screen.
  useEffect(() => {
    if (previewMode !== 'full') return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPreviewMode('split');
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewMode]);
  const startResize = (e: React.PointerEvent) => {
    if (!area) return;
    e.preventDefault();
    const right = area.left + area.width;
    const onMove = (ev: PointerEvent) => {
      const ratio = (right - ev.clientX) / area.width;
      setPreviewRatio(Math.min(0.72, Math.max(0.28, ratio)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const resizeByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setPreviewRatio(r => Math.min(0.72, r + 0.03));
    if (e.key === 'ArrowRight') setPreviewRatio(r => Math.max(0.28, r - 0.03));
  };
  const [confirmation, setConfirmation] = useState<string | null>(null);
  /** Template metadata — only edited in template mode. */
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Seed the form from the template being edited.
  useEffect(() => {
    if (!templateMode || !editingTemplate) return;
    setCategory(getTemplateCategory(editingTemplate));
    setTemplateName(editingTemplate.name[lang]);
    setTemplateDescription(editingTemplate.description[lang]);
    applyTemplate(editingTemplate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMode, editingTemplate]);

  // Seed the form from the notice being edited.
  useEffect(() => {
    if (!editingNotice) return;
    setTitle(editingNotice.title);
    setCategory(editingNotice.category);
    const seeded = {
      ...EMPTY_CHANNELS
    };
    (Object.keys(seeded) as (keyof ChannelsState)[]).forEach(key => {
      seeded[key] = {
        ...seeded[key],
        active: editingNotice.channels.includes(key as never)
      } as never;
    });
    setChannelsByLang({
      fr: seeded,
      en: seeded
    });
    const editedSectors = editingNotice.audience.kind === 'sector' ? sectorsFor(getActiveCity()).filter(s => editingNotice.audience.detail?.fr.includes(s.label.fr.replace(/^Secteurs? /, ''))).map(s => s.id) : [];
    setAudience(() => ({
      ...EMPTY_AUDIENCE,
      scope: editedSectors.length > 0 ? 'places' : 'all',
      places: editedSectors.map(sectorId => ({
        id: `pl-${sectorId}`,
        kind: 'sector' as const,
        sectorId
      }))
    }));
    setSchedule({
      ...EMPTY_SCHEDULE,
      mode: editingNotice.status === 'scheduled' ? 'scheduled' : 'now',
      date: editingNotice.status === 'scheduled' ? editingNotice.date.slice(0, 10) : '',
      time: editingNotice.status === 'scheduled' ? editingNotice.date.slice(11, 16) : '09:00'
    });
    const firstChannel = editingNotice.channels[0];
    if (firstChannel && firstChannel !== 'social') {
      setPreviewTab(firstChannel as PreviewTab);
    }
  }, [editingNotice]);
  const audienceTotal = useMemo(() => getAudienceTotal(audience), [audience]);
  const audienceLabel = describeAudience(audience, lang);

  /** Everything the assistant reasons about, rebuilt as the notice changes. */
  const aiContext: AiContext = useMemo(() => ({
    title,
    category,
    categoryLabel: category ? getCategoryLabel(category)[lang] : '',
    audienceTotal,
    audienceLabel,
    channels,
    variables,
    contentLang
  }), [title, category, lang, audienceTotal, audienceLabel, channels, variables, contentLang]);
  const otherLang: 'fr' | 'en' = contentLang === 'fr' ? 'en' : 'fr';

  /** Active channels that already hold content, per language version. */
  const filledIn = (state: ChannelsState) => (Object.keys(state) as (keyof ChannelsState)[]).filter(key => state[key].active && !isChannelEmpty(key, state));
  const langStatus = useMemo(() => ({
    fr: filledIn(channelsByLang.fr).length > 0,
    en: filledIn(channelsByLang.en).length > 0
  }), [channelsByLang]);

  /** One version is written and the other is still untouched. */
  const canGenerateOtherLang = langStatus[contentLang] && !langStatus[otherLang];

  /**
   * Writes the missing language version from the one already completed, then
   * switches the editor over to it so it can be reviewed.
   */
  const generateOtherLang = () => {
    const source = channelsByLang[contentLang];
    const sourceCtx: AiContext = {
      ...aiContext,
      contentLang: otherLang
    };
    setChannelsByLang(prev => {
      const target = {
        ...prev[otherLang]
      };
      filledIn(source).forEach(key => {
        target[key] = {
          ...target[key],
          active: true,
          ...(generateForChannel(key, sourceCtx) as object)
        } as never;
      });
      return {
        ...prev,
        [otherLang]: target
      };
    });
    onContentLangChange(otherLang);
    setConfirmation(otherLang === 'en' ? t('Version anglaise générée', 'English version generated') : t('Version française générée', 'French version generated'));
    window.setTimeout(() => setConfirmation(null), 4000);
  };
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [findings, setFindings] = useState<AuditFinding[]>([]);

  /** Fills the whole form from what ATNA wrote, then jumps to its main channel. */
  const applyAtnaDraft = (draft: AtnaDraft) => {
    setTitle(draft.title);
    setCategory(draft.category);
    setSelectedTemplate(null);
    setChannels(draft.channels);
    setAudience(prev => ({
      ...prev,
      ...draft.audience
    }));
    const firstChannel = (Object.keys(draft.channels) as (keyof typeof draft.channels)[]).find(key => draft.channels[key].active && key !== 'social');
    if (firstChannel) setPreviewTab(firstChannel as PreviewTab);
  };
  const runReview = () => {
    setReviewOpen(true);
    setReviewLoading(true);
    window.setTimeout(() => {
      setFindings(auditNotice(aiContext));
      setReviewLoading(false);
    }, 900);
  };
  const availableTemplates = useMemo(() => {
    if (!category) return [];
    // Team templates first, then the built-in catalogue.
    return templates.filter(tpl => getTemplateCategory(tpl) === category).sort((a, b) => Number(Boolean(b.custom)) - Number(Boolean(a.custom)));
  }, [category, templates]);
  const patchAudience = (patch: Partial<AudienceState>) => setAudience(prev => ({
    ...prev,
    ...patch
  }));
  const patchSchedule = (patch: Partial<ScheduleState>) => setSchedule(prev => ({
    ...prev,
    ...patch
  }));

  /** Channel selection belongs to the notice, so it applies to both versions. */
  const toggleChannel = (key: keyof ChannelsState) => {
    const willActivate = !channels[key].active;
    setChannelsByLang(prev => ({
      fr: {
        ...prev.fr,
        [key]: {
          ...prev.fr[key],
          active: willActivate
        }
      },
      en: {
        ...prev.en,
        [key]: {
          ...prev.en[key],
          active: willActivate
        }
      }
    }));
    if (willActivate) setPreviewTab(key as PreviewTab);
  };
  const updateChannel = <K extends keyof ChannelsState,>(key: K, patch: Partial<ChannelsState[K]>) => setChannels(prev => ({
    ...prev,
    [key]: {
      ...prev[key],
      ...patch
    }
  }));
  const applyTemplate = (tpl: NoticeTemplate) => {
    setSelectedTemplate(tpl.id);
    setTitle(tpl.title);
    setDescription(tpl.push.message);
    setChannels(prev => ({
      ...prev,
      email: {
        active: true,
        subject: tpl.email.subject,
        body: textToHtml(tpl.email.body, tpl.imageUrl),
        attachments: []
      },
      sms: {
        active: true,
        text: tpl.sms
      },
      push: {
        active: true,
        title: tpl.push.title,
        message: tpl.push.message
      },
      social: tpl.social ? {
        active: true,
        postText: textToHtml(tpl.social, tpl.imageUrl)
      } : prev.social
    }));
    setPreviewTab('push');
  };
  const hasAnyContent = Boolean(title.trim()) || langStatus.fr || langStatus.en;

  /** Variables used in the content but left without a value for this language. */
  const incompleteVariables = useMemo(() => getIncompleteVariables(variables, channels, contentLang), [variables, channels, contentLang]);
  const canSubmit = templateMode ? Boolean(category) && Boolean(templateName.trim()) && hasAnyContent : incompleteVariables.length === 0 && (schedule.mode === 'scheduled' ? Boolean(schedule.date) && hasAnyContent : hasAnyContent);
  const submitLabel = templateMode ? editingTemplate ? t('Enregistrer les modifications', 'Save changes') : t('Créer le modèle', 'Create template') : schedule.mode === 'scheduled' ? t("Programmer l'envoi", 'Schedule sending') : editingNotice ? t('Enregistrer et envoyer', 'Save and send') : t('Envoyer maintenant', 'Send now');
  const SubmitIcon = templateMode ? BookmarkPlusIcon : schedule.mode === 'scheduled' ? CalendarClockIcon : SendIcon;

  /** Snapshot of the current content as a reusable template. */
  const buildTemplate = (id: string, name: string, templateDesc: string): NoticeTemplate => ({
    id,
    name: {
      fr: name,
      en: name
    },
    description: {
      fr: templateDesc || 'Modèle enregistré par votre équipe',
      en: templateDesc || 'Template saved by your team'
    },
    title,
    email: {
      subject: channels.email.subject,
      body: stripHtml(channels.email.body)
    },
    sms: stripHtml(channels.sms.text),
    push: {
      title: stripHtml(channels.push.title),
      message: stripHtml(channels.push.message)
    },
    social: stripHtml(channels.social.postText) || undefined,
    custom: true
  });
  const closeSaveDialog = useCallback(() => setSaveDialogOpen(false), []);

  /** "Save as template" from the notice composer — the notice stays open. */
  const saveNoticeAsTemplate = (name: string, templateDesc: string) => {
    onSaveTemplate(buildTemplate(`${category}:${Date.now()}`, name, templateDesc));
    setSaveDialogOpen(false);
    setConfirmation(t(`Modèle « ${name} » enregistré`, `Template “${name}” saved`));
    window.setTimeout(() => setConfirmation(null), 4000);
  };
  const handleSubmit = () => {
    if (!canSubmit) return;
    if (templateMode) {
      // Keep the template's identity when editing, even if its category moves.
      const suffix = editingTemplate ? editingTemplate.id.split(':').slice(1).join(':') : String(Date.now());
      const saved = buildTemplate(`${category}:${suffix}`, templateName.trim(), templateDescription.trim());
      onSaveTemplate(saved, editingTemplate?.id);
      onSubmit('draft');
      return;
    }
    onSubmit('send');
  };
  return <div ref={rootRef} className="font-['Inter'] bg-[#F7F7F8] pb-28" data-id="element-4496" data-mp="2ikmtj92aqyfz">
      <div className={`mx-auto grid grid-cols-1 gap-8 items-start ${docked ? 'max-w-3xl' : 'max-w-6xl lg:grid-cols-[1fr_360px]'}`} data-id="element-4497" data-mp="32ly7wlc1ufy2">
        {/* LEFT — FORM */}
        <div className="space-y-10 min-w-0" data-id="element-4498" data-mp="3bji9jvdtqe97">
          {/* 1. ATNA BRIEF */}
          {!templateMode && <AtnaBrief channels={channels} contentLang={contentLang} onGenerate={applyAtnaDraft} data-id="element-4499" data-mp="3gd2xeulpdruo" />}

          {/* 2. GENERAL INFORMATION */}
          <section className="space-y-6" data-id="element-4500" data-mp="3q84qg1j3ne2j">
            <div className="flex items-center justify-between gap-4" data-id="element-4501" data-mp="3v8iy3igsgm01">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-4502" data-mp="3v77upjr99g9g">
                {templateMode ? t('Informations du modèle', 'Template information') : t('Informations générales', 'General information')}
              </h2>
              <label className={`flex items-center gap-2 cursor-pointer select-none ${templateMode ? 'hidden' : ''}`} data-id="element-4503" data-mp="24xec07o3l0vz">
                <span className="text-xs font-medium text-slate-400" data-id="element-4504" data-mp="1slc7mi9xd881">
                  {t('Version classique', 'Classic version')}
                </span>
                <button type="button" role="switch" aria-checked={legacyMode} aria-label={t('Version classique', 'Classic version')} onClick={() => onToggleLegacy(!legacyMode)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${legacyMode ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-4505" data-mp="1jlp67wh742ez">
                  <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${legacyMode ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-4506" data-mp="qwi9n31axmwd" />
                </button>
              </label>
            </div>

            <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-6" data-id="element-4507" data-mp="39n2kodfxsw2l">
              {templateMode && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b border-slate-100" data-id="element-4508" data-mp="a64b5cwh4ypw">
                  <div data-id="element-4509" data-mp="6754nnv3gtwc">
                    <label htmlFor="template-name" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4510" data-mp="63ppkl1f7kux">
                      {t('Nom du modèle', 'Template name')}
                    </label>
                    <input id="template-name" type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder={t('Ex. : Fermeture de rue', 'E.g. Street closure')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4511" data-mp="2lxzvmkiz40y2" />
                  </div>
                  <div data-id="element-4512" data-mp="2pkg3b3kkcaug">
                    <label htmlFor="template-description" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4513" data-mp="2pl7p5mocyu7j">
                      {t('Description', 'Description')}{' '}
                      <span className="font-normal text-slate-400" data-id="element-4514" data-mp="2zjdnxvevbk86">
                        {t('(facultatif)', '(optional)')}
                      </span>
                    </label>
                    <input id="template-description" type="text" value={templateDescription} onChange={e => setTemplateDescription(e.target.value)} placeholder={t('Quand l’utiliser', 'When to use it')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4515" data-mp="155w86ukpfrnp" />
                  </div>
                </div>}
              <div data-id="element-4516" data-mp="1l0j6qrncvuj9">
                <label htmlFor="notice-title" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4517" data-mp="1kzrcbk5hw2uk">
                  {templateMode ? t('Titre de l’avis généré', 'Title of the generated notice') : t('Titre de l’avis', 'Notice title')}
                </label>
                <input id="notice-title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('Ex. : Avis d’ébullition d’eau préventif', 'E.g. Preventive boil water advisory')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4518" data-mp="23rk3d9107dzn" />
              </div>

              {/* Category grid */}
              <div data-id="element-4519" data-mp="1zn88l052yjst">
                <span className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4520" data-mp="1zmq117cywj5l">
                  {t('Catégorie', 'Category')}
                </span>
                <div role="radiogroup" aria-label={t('Catégorie', 'Category')} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-id="element-4521" data-mp="1qowakd1vwjqc">
                  {CATEGORIES.map(c => {
                  const Icon = c.icon;
                  const active = category === c.value;
                  return <button key={c.value} type="button" role="radio" aria-checked={active} onClick={() => {
                    setCategory(c.value);
                    setSelectedTemplate(null);
                  }} className={`flex flex-col items-center justify-center gap-2 px-2 py-3.5 rounded-xl border text-center transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/50 ring-1 ring-[#4F46E5]' : 'border-slate-200 bg-white hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4522" data-mp="2o2fwawq280hq">
                        <span className={`inline-flex w-9 h-9 rounded-lg items-center justify-center ${active ? 'bg-[#4F46E5] text-white' : c.urgent ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-500'}`} data-id="element-4523" data-mp="3qordvmo2c5fk">
                          <Icon className="w-4 h-4" data-id="element-4524" data-mp="6v7hzkswkaic" />
                        </span>
                        <span className={`text-xs font-medium leading-tight ${active ? 'text-[#4F46E5]' : 'text-slate-600'}`} data-id="element-4525" data-mp="brxvq1h8iu0l">
                          {c.label[lang]}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* Template suggestions */}
              <AnimatePresence data-id="element-4526" data-mp="zik1tn7oi47y">
                {category && !templateMode && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-4527" data-mp="zkwhtutvm4id">
                    <div className="pt-1" data-id="element-4528" data-mp="39lptcg38bnwe">
                      <div className="flex items-center gap-2 mb-3" data-id="element-4529" data-mp="39kogc62nkrc1">
                        <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-4530" data-mp="34nfy65hi8nas" />
                        <span className="block text-sm font-medium text-slate-700" data-id="element-4531" data-mp="2gwahjo3qpqzf">
                          {t('Modèles suggérés', 'Suggested templates')}
                        </span>
                      </div>
                      {availableTemplates.length > 0 ? <div className="space-y-2" data-id="element-4532" data-mp="2qogbh3djyih9">
                          {availableTemplates.map(tpl => {
                      const active = selectedTemplate === tpl.id;
                      return <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)} className={`w-full flex items-center gap-3 text-left p-3 rounded-lg border transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/40' : 'border-slate-200 hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4533" data-mp="1t7rblf85xqai">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-4534" data-mp="wf1bs6mqgzim">
                                  {active ? <CheckIcon className="w-4 h-4" data-id="element-4535" data-mp="3827mzxbtu4o0" /> : tpl.custom ? <StarIcon className="w-4 h-4" data-id="element-4536" data-mp="3usser1u0w4q4" /> : <FileTextIcon className="w-4 h-4" data-id="element-4537" data-mp="3ptfr4evbutmh" />}
                                </div>
                                <div className="min-w-0 flex-1" data-id="element-4538" data-mp="3kw5uae5lzfom">
                                  <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2" data-id="element-4539" data-mp="mdif00dlvbli">
                                    {tpl.name[lang]}
                                    {tpl.custom && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-[#4F46E5] text-[10px] font-bold uppercase tracking-wide" data-id="element-4540" data-mp="clut0eztimrf">
                                        {t('Modèle', 'Template')}
                                      </span>}
                                  </p>
                                  <p className="text-xs text-slate-500 truncate" data-id="element-4541" data-mp="10ax93iet07jt">
                                    {tpl.description[lang]}
                                  </p>
                                </div>
                              </button>;
                    })}
                          <p className="text-xs text-slate-400 pt-1" data-id="element-4542" data-mp="1ml5m089ag4ll">
                            {t('Cliquez sur un modèle pour remplir automatiquement le contenu des canaux.', 'Click a template to automatically fill in the channel content.')}
                          </p>
                        </div> : <p className="text-sm text-slate-400 py-2" data-id="element-4543" data-mp="25bwtf49ptkxc">
                          {t('Aucun modèle disponible pour cette catégorie. Rédigez votre contenu dans les canaux ci-dessous.', 'No template available for this category. Write your content in the channels below.')}
                        </p>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </section>

          {/* 3. AUDIENCE & 4. VARIABLES — notice-only */}
          {!templateMode && <>
              <AudienceSelector audience={audience} onChange={patchAudience} audienceTotal={audienceTotal} data-id="element-4544" data-mp="3mkqxfx2tzoi" />

              {/* Custom variables temporarily hidden — flip to re-enable. */}
              {SHOW_CUSTOM_VARIABLES && <VariablesBlock variables={variables} onChange={setVariables} channels={channels} contentLang={contentLang} data-id="element-4545" data-mp="3qxu487jdhl5n" />}
            </>}

          {/* 5. CHANNELS */}
          <ChannelEditor channels={channels} onToggle={toggleChannel} onUpdate={updateChannel} audienceTotal={audienceTotal} variables={variables} contentLang={contentLang} onContentLangChange={onContentLangChange} langStatus={langStatus} canGenerateOtherLang={canGenerateOtherLang} onGenerateOtherLang={generateOtherLang} aiContext={aiContext} data-id="element-4546" data-mp="re9obaor9ecb" />

          {/* 6. SENDING TIME */}
          {!templateMode && <SendScheduler schedule={schedule} onChange={patchSchedule} categoryLabel={category ? getCategoryLabel(category)[lang] : null} data-id="element-4547" data-mp="1o5eowgcxdy00" />}
        </div>

        {/* RIGHT — LIVE PREVIEW (inline on narrow screens) */}
        {!docked && <div className="lg:sticky lg:top-8" data-id="element-4548" data-mp="3efzsa8856qoz">
            <NoticePreview channels={channels} title={title} description={description} categoryLabel={category ? getCategoryLabel(category)[lang] : t('Général', 'General')} previewTab={previewTab} onPreviewTabChange={setPreviewTab} variables={variables} contentLang={contentLang} data-id="element-4549" data-mp="3eivuw9uxq3jr" />
          </div>}
      </div>

      {/* CLOSED PREVIEW — compact card at the top right */}
      {docked && area && previewMode === 'closed' && <div className="fixed z-40" style={{
      top: area.top + 16,
      right: 16
    }} data-id="element-4550" data-mp="2xvzqawx1e40d">
          <PreviewMiniPanel channels={channels} title={title} categoryLabel={category ? getCategoryLabel(category)[lang] : t('Général', 'General')} contentLang={contentLang} onOpen={tab => {
        if (tab) setPreviewTab(tab);
        setPreviewMode('split');
      }} data-id="element-4551" data-mp="32syd0uedf8oj" />
        </div>}

      {/* DOCKED PREVIEW — full-height panel, resizable, closable, full screen */}
      {docked && area && previewMode !== 'closed' && <aside className="fixed bottom-0 right-0 z-40 bg-white border-l border-slate-200 shadow-[-8px_0_24px_-16px_rgba(15,23,42,0.18)]" style={{
      top: area.top,
      width: previewMode === 'full' ? area.width : panelWidth
    }} data-id="element-4552" data-mp="117lyf06i7gow">
          {previewMode === 'split' && <div role="separator" aria-orientation="vertical" aria-label={t('Redimensionner l’aperçu', 'Resize preview')} aria-valuenow={Math.round(previewRatio * 100)} aria-valuemin={28} aria-valuemax={72} tabIndex={0} onPointerDown={startResize} onKeyDown={resizeByKey} onDoubleClick={() => setPreviewRatio(0.45)} className="group absolute inset-y-0 -left-1.5 w-3 cursor-col-resize z-10 flex justify-center focus:outline-none" data-id="element-4553" data-mp="naqo74einj79">
              <span className="w-px h-full bg-transparent group-hover:bg-[#4F46E5] group-focus-visible:bg-[#4F46E5] group-active:bg-[#4F46E5] transition-colors" data-id="element-4554" data-mp="3qr62sx9hubol" />
            </div>}
          <NoticePreview channels={channels} title={title} description={description} categoryLabel={category ? getCategoryLabel(category)[lang] : t('Général', 'General')} previewTab={previewTab} onPreviewTabChange={setPreviewTab} variables={variables} contentLang={contentLang} docked expanded={previewMode === 'full' || panelWidth >= 620} fullscreen={previewMode === 'full'} onToggleFullscreen={() => setPreviewMode(m => m === 'full' ? 'split' : 'full')} onClose={() => setPreviewMode('closed')} data-id="element-4555" data-mp="1zn490h01azci" />
        </aside>}

      {/* ACTION BAR */}
      <div className="sticky bottom-0 mt-10 -mb-28 bg-white/95 backdrop-blur border-t border-slate-200 z-30" data-id="element-4556" data-mp="2w9h8jt1zrfkw">
        <div className={`${docked ? 'max-w-3xl' : 'max-w-6xl'} mx-auto px-1 py-3 flex flex-wrap items-center gap-3 justify-between`} data-id="element-4557" data-mp="pms3sfxtaoz1">
          <div className="min-w-0 flex items-center gap-3" role="status" aria-live="polite" data-id="element-4558" data-mp="kpjk2ax0mcvo">

            {incompleteVariables.length > 0 && <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg" data-id="element-4559" data-mp="umeh0pdn457u">
                <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-4560" data-mp="18bq11mof32y1" />
                {incompleteVariables.length}{' '}
                {t('variable(s) sans valeur : ', 'variable(s) without a value: ')}
                <span className="font-semibold" data-id="element-4561" data-mp="1iaxlsuxhdzv6">
                  {incompleteVariables.map(v => v.label[lang]).join(', ')}
                </span>
              </span>}
            {confirmation && incompleteVariables.length === 0 && <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg" data-id="element-4562" data-mp="3f4sglq9p3mhn">
                <CheckIcon className="w-4 h-4" data-id="element-4563" data-mp="1j37868zmlvd3" />
                {confirmation}
              </span>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-4564" data-mp="1s110z9y6m6vi">
            {templateMode ? <button type="button" onClick={() => onSubmit('draft')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4565" data-mp="1x158iah43r9l">
                {t('Annuler', 'Cancel')}
              </button> : <>
                <button type="button" onClick={runReview} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-4566" data-mp="2frd66d21lpie">
                  <SparklesIcon className="w-4 h-4" data-id="element-4567" data-mp="2zthroreo1yj5" />
                  {t("Vérifier l'avis", 'Review the notice')}
                </button>

                <button type="button" onClick={() => setSaveDialogOpen(true)} disabled={!hasAnyContent} title={hasAnyContent ? undefined : t('Rédigez du contenu d’abord', 'Write some content first')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4568" data-mp="2ustrpg8gm7cs">
                  <BookmarkPlusIcon className="w-4 h-4" data-id="element-4569" data-mp="34qhyx5n957bu" />
                  {t('Enregistrer comme modèle', 'Save as template')}
                </button>

                <button type="button" onClick={() => onSubmit('draft')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4570" data-mp="d1m79o3xjox">
                  <SaveIcon className="w-4 h-4" data-id="element-4571" data-mp="a5idxtr07q8b" />
                  {t('Enregistrer le brouillon', 'Save draft')}
                </button>
              </>}

            <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#4F46E5]" data-id="element-4572" data-mp="12scvoiwfsbi3">
              <SubmitIcon className="w-4 h-4" data-id="element-4573" data-mp="3dkxocw3ao1tz" />
              {submitLabel}
            </button>
          </div>
        </div>
      </div>

      <AiReviewPanel open={reviewOpen} loading={reviewLoading} findings={findings} onClose={() => setReviewOpen(false)} data-id="element-4574" data-mp="3ikjxatc3avwz" />

      <SaveTemplateDialog open={saveDialogOpen} categoryLabel={category ? getCategoryLabel(category)[lang] : null} defaultName={title} onClose={closeSaveDialog} onSave={saveNoticeAsTemplate} data-id="element-4575" data-mp="1vnayf082htzc" />
    </div>;
}